import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Edit, Save, Cancel, Refresh } from '@mui/icons-material';
import { usersAPI } from '../services/api';

interface UserRequestInfo {
  user_id: string;
  email: string;
  name: string;
  monthly_request_limit?: number;
  current_month_requests: number;
  remaining_requests?: number;
  last_request_reset: string;
}

const RequestLimitsManagement: React.FC = () => {
  const [users, setUsers] = useState<UserRequestInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [newLimit, setNewLimit] = useState<number | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getRequestLimits();
      setUsers(response);
    } catch (error) {
      console.error('Error loading users:', error);
      setSnackbarMessage('Eroare la încărcarea utilizatorilor');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEditLimit = (userId: string, currentLimit?: number) => {
    setSelectedUserId(userId);
    setNewLimit(currentLimit || null);
    setDialogOpen(true);
  };

  const handleSaveLimit = async () => {
    try {
      await usersAPI.updateRequestLimit(selectedUserId, { monthly_request_limit: newLimit === null ? undefined : newLimit });
      setSnackbarMessage('Limita de cereri a fost actualizată cu succes');
      setSnackbarOpen(true);
      setDialogOpen(false);
      await loadUsers();
    } catch (error) {
      console.error('Error updating limit:', error);
      setSnackbarMessage('Eroare la actualizarea limitei');
      setSnackbarOpen(true);
    }
  };

  const handleResetAllRequests = async () => {
    try {
      await usersAPI.resetMonthlyRequests();
      setSnackbarMessage('Cererile lunare au fost resetate pentru toți utilizatorii');
      setSnackbarOpen(true);
      await loadUsers();
    } catch (error) {
      console.error('Error resetting requests:', error);
      setSnackbarMessage('Eroare la resetarea cererilor');
      setSnackbarOpen(true);
    }
  };

  const getLimitText = (limit?: number) => {
    if (limit === null || limit === undefined) return 'Nelimitat';
    if (limit === 0) return 'Dezactivat';
    return `${limit} cereri/lună`;
  };

  const getRemainingText = (remaining?: number) => {
    if (remaining === null || remaining === undefined) return 'Nelimitat';
    if (remaining === 0) return '0 cereri';
    return `${remaining} cereri`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Gestionarea Limitelor de Cereri
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleResetAllRequests}
              color="warning"
            >
              Resetează Cererile Lunare
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Gestionează limitele de cereri OpenAI pentru utilizatori. Limitele se resetează automat la începutul fiecărei luni.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Utilizator</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Limita Lunară</TableCell>
                  <TableCell>Cereri Această Lună</TableCell>
                  <TableCell>Cereri Rămase</TableCell>
                  <TableCell>Ultima Resetare</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getLimitText(user.monthly_request_limit)}
                        color={user.monthly_request_limit === 0 ? 'error' : user.monthly_request_limit === null ? 'info' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{user.current_month_requests}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getRemainingText(user.remaining_requests)}
                        color={user.remaining_requests === 0 ? 'error' : user.remaining_requests === null ? 'info' : 'success'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.last_request_reset).toLocaleDateString('ro-RO')}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleEditLimit(user.user_id, user.monthly_request_limit || undefined)}
                      >
                        Editează
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit Limit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editează Limita de Cereri</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Limita Lunară</InputLabel>
              <Select
                value={newLimit === null ? 'unlimited' : newLimit === 0 ? 'disabled' : newLimit}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'unlimited') setNewLimit(null);
                  else if (value === 'disabled') setNewLimit(0);
                  else setNewLimit(Number(value));
                }}
                label="Limita Lunară"
              >
                <MenuItem value="unlimited">Nelimitat</MenuItem>
                <MenuItem value="disabled">Dezactivat (0 cereri)</MenuItem>
                <MenuItem value={5}>5 cereri/lună</MenuItem>
                <MenuItem value={10}>10 cereri/lună</MenuItem>
                <MenuItem value={20}>20 cereri/lună</MenuItem>
                <MenuItem value={50}>50 cereri/lună</MenuItem>
                <MenuItem value={100}>100 cereri/lună</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} startIcon={<Cancel />}>
            Anulează
          </Button>
          <Button onClick={handleSaveLimit} startIcon={<Save />} variant="contained">
            Salvează
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default RequestLimitsManagement;
